import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { Card, NumberField, SaveBar, MissingConfigBanner } from '../components/Form';
import { useConfigDoc, useWriteConfig } from '../lib/configHooks';
import { useToast } from '../components/Toast';

interface PricingConfig {
  [key: string]: unknown;
  basePayFlat?: number;
  payPerMile?: number;
  payPerMinute?: number;
  minimumPay?: number;
  driverPercentage?: number;
  platformFeeRate?: number;
  tipDriverPercentage?: number;
  surgeMultiplier?: number;
}

export default function PricingPage() {
  const { data } = useConfigDoc<PricingConfig>('pricing');
  const write = useWriteConfig('pricing');
  const [draft, setDraft] = useState<PricingConfig>({});
  const [dirty, setDirty] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!dirty) setDraft(data || {});
  }, [data, dirty]);

  const update = <K extends keyof PricingConfig>(k: K, v: PricingConfig[K]) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setDirty(true);
  };

  const onSave = async () => {
    try {
      await write.mutateAsync(draft);
      setDirty(false);
      toast.success('Pricing saved.');
    } catch (e) {
      toast.error(`Save failed: ${e instanceof Error ? e.message : e}`);
    }
  };

  const splitsValid =
    Math.abs(
      (draft.driverPercentage ?? 0) + (draft.platformFeeRate ?? 0) - 1
    ) < 0.001;

  return (
    <>
      <PageHeader
        title="Pricing"
        subtitle="Base fare, per-mile / per-minute rates, surge, and revenue split."
      />
      {data && Object.keys(data).length === 0 && (
        <MissingConfigBanner docIds={['pricing']} />
      )}
      <Card title="Base rates">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <NumberField
            label="Base pay"
            value={draft.basePayFlat}
            onChange={(v) => update('basePayFlat', v)}
            step={0.05}
            min={0}
            suffix="$"
            hint="Flat amount per delivery."
          />
          <NumberField
            label="Per mile"
            value={draft.payPerMile}
            onChange={(v) => update('payPerMile', v)}
            step={0.05}
            min={0}
            suffix="$/mi"
          />
          <NumberField
            label="Per minute"
            value={draft.payPerMinute}
            onChange={(v) => update('payPerMinute', v)}
            step={0.01}
            min={0}
            suffix="$/min"
          />
          <NumberField
            label="Minimum pay"
            value={draft.minimumPay}
            onChange={(v) => update('minimumPay', v)}
            step={0.05}
            min={0}
            suffix="$"
          />
        </div>
      </Card>
      <Card title="Surge">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NumberField
            label="Surge multiplier"
            value={draft.surgeMultiplier}
            onChange={(v) => update('surgeMultiplier', v)}
            step={0.05}
            min={1}
            max={5}
            hint="Applied to subtotal. 1.0 = no surge."
          />
        </div>
      </Card>
      <Card title="Revenue split">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <NumberField
            label="Driver % of fare"
            value={draft.driverPercentage}
            onChange={(v) => update('driverPercentage', v)}
            step={0.01}
            min={0}
            max={1}
            hint="0.80 = 80%"
          />
          <NumberField
            label="Platform fee rate"
            value={draft.platformFeeRate}
            onChange={(v) => update('platformFeeRate', v)}
            step={0.01}
            min={0}
            max={1}
          />
          <NumberField
            label="Tip → driver %"
            value={draft.tipDriverPercentage}
            onChange={(v) => update('tipDriverPercentage', v)}
            step={0.01}
            min={0}
            max={1}
            hint="1.0 = driver keeps 100% of tip."
          />
        </div>
        {!splitsValid && dirty && (
          <p className="mt-3 text-sm text-amber-700">
            Driver % + Platform fee should equal 1.0 (currently{' '}
            {((draft.driverPercentage ?? 0) + (draft.platformFeeRate ?? 0)).toFixed(
              2
            )}
            ).
          </p>
        )}
      </Card>
      <SaveBar
        dirty={dirty}
        saving={write.isPending}
        onSave={onSave}
        onReset={() => {
          setDraft(data || {});
          setDirty(false);
        }}
        warning="Pricing changes affect customer quotes and driver earnings within 5 minutes."
      />
    </>
  );
}
